import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useStore } from './stores/store';
import { getPermissionForPath, hasPermission } from './services/auth';
import { PageLoader } from './components/LoadingSpinner';

const Layout = lazy(() => import('./components/Layout'));
const Login = lazy(() => import('./pages/Login'));

// ── Existing pages kept as destination components (rename / re-skin-only;
//    Stage C re-skins or wraps in tab shells IN PLACE — data layer unchanged).
const Dashboard = lazy(() => import('./pages/Dashboard'));            // Today (/)
const Tickets = lazy(() => import('./pages/Tickets'));               // Conversations
const TicketDetail = lazy(() => import('./pages/TicketDetail'));
const Refunds = lazy(() => import('./pages/Refunds'));               // Refunds shell base
const RefundDetail = lazy(() => import('./pages/RefundDetail'));
const PaymentHolds = lazy(() => import('./pages/PaymentHolds'));     // Payment Holds (built)
const Disputes = lazy(() => import('./pages/Disputes'));             // Disputes
const Operations = lazy(() => import('./pages/Operations'));         // Order Ops shell base
const OperationDetail = lazy(() => import('./pages/OperationDetail'));
const OrderChangeDetail = lazy(() => import('./pages/OrderChangeDetail'));
const HeldOrders = lazy(() => import('./pages/HeldOrders'));         // Shipping Holds
const Returns = lazy(() => import('./pages/Returns'));               // Returns
const ReturnDetail = lazy(() => import('./pages/ReturnDetail'));
const Emma = lazy(() => import('./pages/Emma'));                     // Performance shell base
const AIFeedback = lazy(() => import('./pages/AIFeedback'));         // Reviews shell base
const AIFeedbackDetail = lazy(() => import('./pages/AIFeedbackDetail'));
const TestingLab = lazy(() => import('./pages/TestingLab'));         // Playground
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));   // Brain shell base
const AIAgent = lazy(() => import('./pages/AIAgent'));               // Controls
const AICosts = lazy(() => import('./pages/AICosts'));               // Spend (admin)
const Exports = lazy(() => import('./pages/Exports'));               // Exports
const UserManagement = lazy(() => import('./pages/UserManagement')); // Team (admin)
const UserDetail = lazy(() => import('./pages/UserDetail'));
const Settings = lazy(() => import('./pages/Settings'));             // account menu

// ── Night Ops STUB destinations (Stage C fleshes these out). New surfaces or
//    merges without a single surviving shell — a kit Panel + NightEmpty so the
//    route resolves and the build passes.
const QueueStub = lazy(() => import('./pages/Queue'));               // MERGE: Inbox+Alerts+Actions+Followups
const Guide = lazy(() => import('./pages/Guide'));                   // NEW
const AskEmma = lazy(() => import('./pages/AskEmma'));               // NEW
const Discover = lazy(() => import('./pages/Discover'));             // NEW — Search & Discover

// ── Public pages
const PublicFeedback = lazy(() => import('./pages/PublicFeedback'));
const QuickFeedback = lazy(() => import('./pages/QuickFeedback'));
const GoRedirect = lazy(() => import('./pages/GoRedirect'));

const DEFAULT_LANDING = {
  dashboard: '/',
  tickets: '/conversations',
  alerts: '/queue',
  returns: '/returns',
  operations: '/order-ops',
  inbox: '/queue',
  knowledge_base: '/brain',
  ai_agent: '/controls',
  ai_playground: '/playground',
  analytics: '/performance',
  ai_feedback: '/reviews',
  ai_costs: '/spend',
  settings: '/settings',
  handbook: '/brain?tab=handbook',
  users: '/team',
};

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, token } = useStore();
  const location = useLocation();

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only pages (00/A8): Team (/team incl. /team/:id), Spend (/spend),
  // legacy /users & /admin, and the Refunds "Safeguards" tab
  // (/refunds?tab=safeguards). Non-admins are bounced to their profile.
  const path = location.pathname;
  const search = location.search || '';
  const adminGated =
    path === '/team' || path.startsWith('/team/') ||
    path === '/spend' ||
    path.startsWith('/users') || path.startsWith('/admin') ||
    path === '/ai-costs' ||
    (path === '/refunds' && /(?:[?&])tab=safeguards\b/.test(search)) ||
    path === '/refunds/safeguards';
  if (adminGated && user.role !== 'admin') {
    return <Navigate to="/settings" replace />;
  }

  const perm = getPermissionForPath(location.pathname);
  if (perm && !hasPermission(perm)) {
    const firstAllowed = ['operations', 'returns', 'handbook', 'settings', 'dashboard', 'tickets'].find((p) => hasPermission(p));
    const to = firstAllowed ? (DEFAULT_LANDING[firstAllowed] || '/') : '/';
    return <Navigate to={to} replace />;
  }

  return children;
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes (outside Layout, stay LIGHT per 00/A7). The `.light`
            wrapper re-maps --primary (and other flipped tokens) back to the
            light palette so the forced-dark <html class="dark"> can't leak an
            ice-cyan submit button onto these customer-facing forms. */}
        <Route path="/login" element={<Login />} />
        <Route path="/feedback" element={<div className="light"><PublicFeedback /></div>} />
        <Route path="/quick-feedback" element={<div className="light"><QuickFeedback /></div>} />
        <Route path="/go/:type" element={<GoRedirect />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* ── OVERSEE ── */}
          <Route index element={<Dashboard />} />
          <Route path="guide" element={<Guide />} />
          <Route path="queue" element={<QueueStub />} />
          <Route path="queue/alerts/:id" element={<QueueStub />} />
          <Route path="conversations" element={<Tickets />} />
          <Route path="conversations/:ticketId" element={<TicketDetail />} />

          {/* ── MONEY ── */}
          <Route path="refunds" element={<Refunds />} />
          <Route path="refunds/:id" element={<RefundDetail />} />
          <Route path="payment-holds" element={<PaymentHolds />} />
          <Route path="disputes" element={<Disputes />} />

          {/* ── ORDERS ── */}
          <Route path="order-ops" element={<Operations />} />
          <Route path="order-ops/:id" element={<OperationDetail />} />
          <Route path="order-ops/order-changes/:id" element={<OrderChangeDetail />} />
          <Route path="shipping-holds" element={<HeldOrders />} />
          <Route path="returns" element={<Returns />} />
          <Route path="returns/:id" element={<ReturnDetail />} />

          {/* ── EMMA ── */}
          <Route path="performance" element={<Emma />} />
          <Route path="reviews" element={<AIFeedback />} />
          <Route path="reviews/feedback/:id" element={<AIFeedbackDetail />} />
          <Route path="playground" element={<TestingLab />} />
          <Route path="brain" element={<KnowledgeBase />} />
          <Route path="controls" element={<AIAgent />} />

          {/* ── SYSTEM TRAY ── */}
          <Route path="spend" element={<AICosts />} />
          <Route path="exports" element={<Exports />} />
          <Route path="team" element={<UserManagement />} />
          <Route path="team/:id" element={<UserDetail />} />
          <Route path="ask" element={<AskEmma />} />
          <Route path="discover" element={<Discover />} />

          {/* Account (not a zone/tray item — reached via the user menu) */}
          <Route path="settings" element={<Settings />} />

          {/* ── 301 redirects (old route → new; spec 03 §5). Redirect ships in
                the SAME change as its destination (00/B3). ── */}
          <Route path="tickets" element={<Navigate to="/conversations" replace />} />
          <Route path="tickets/:ticketId" element={<RedirectTicket />} />
          <Route path="actions" element={<Navigate to="/queue?tab=actions" replace />} />
          <Route path="alerts" element={<Navigate to="/queue?tab=alerts" replace />} />
          <Route path="alerts/:id" element={<RedirectAlert />} />
          <Route path="inbox" element={<Navigate to="/queue?tab=inbox" replace />} />
          <Route path="snooze-followups" element={<Navigate to="/queue?tab=follow-ups" replace />} />
          <Route path="refunds/safeguards" element={<Navigate to="/refunds?tab=safeguards" replace />} />
          <Route path="pending-refunds" element={<Navigate to="/refunds?tab=pending" replace />} />
          <Route path="cc-protection-holds" element={<Navigate to="/payment-holds" replace />} />
          <Route path="disputes/analytics" element={<Navigate to="/disputes?tab=analytics" replace />} />
          <Route path="operations" element={<Navigate to="/order-ops?tab=tasks" replace />} />
          <Route path="tracking-review" element={<Navigate to="/order-ops?tab=tracking-review" replace />} />
          <Route path="operations/:id" element={<RedirectOperation />} />
          <Route path="order-changes" element={<Navigate to="/order-ops?tab=order-changes" replace />} />
          <Route path="order-changes/:id" element={<RedirectOrderChange />} />
          <Route path="held-orders" element={<Navigate to="/shipping-holds" replace />} />
          <Route path="emma" element={<Navigate to="/performance" replace />} />
          <Route path="emma-performance" element={<Navigate to="/performance" replace />} />
          <Route path="haris-audits" element={<Navigate to="/reviews?tab=audits" replace />} />
          <Route path="analytics" element={<Navigate to="/performance?tab=analytics" replace />} />
          <Route path="retention" element={<Navigate to="/performance?tab=retention" replace />} />
          <Route path="ai-feedback" element={<Navigate to="/reviews?tab=feedback" replace />} />
          <Route path="ai-feedback/:id" element={<RedirectFeedback />} />
          <Route path="testing-lab" element={<Navigate to="/playground" replace />} />
          <Route path="knowledge" element={<Navigate to="/brain?tab=faqs" replace />} />
          <Route path="handbook" element={<Navigate to="/brain?tab=handbook" replace />} />
          <Route path="ai-agent" element={<Navigate to="/controls" replace />} />
          <Route path="ai-costs" element={<Navigate to="/spend" replace />} />
          <Route path="users" element={<Navigate to="/team?tab=users" replace />} />
          <Route path="users/:id" element={<RedirectUser />} />
          <Route path="admin" element={<Navigate to="/team?tab=system" replace />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Param-preserving redirects (detail routes → new drawer deep links).
function RedirectTicket() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/conversations/${id}`} replace />;
}
function RedirectAlert() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/queue/alerts/${id}`} replace />;
}
function RedirectOperation() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/order-ops/${id}?tab=tasks`} replace />;
}
function RedirectOrderChange() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/order-ops/order-changes/${id}`} replace />;
}
function RedirectFeedback() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/reviews/feedback/${id}`} replace />;
}
function RedirectUser() {
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];
  return <Navigate to={`/team/${id}`} replace />;
}

export default App;
